import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type { Contact, OrganisationClient } from '~/api/clients/types';

import { useCachedCreditsafeRecord, useCreateContact } from '~/api/clients';
import { Blocker, Container, Header, Separator } from '~/components';
import { Form } from '~/components/Form';
import { bypassBlocker } from '~/constants/router';
import { SearchParamKeys } from '~/constants/url';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import type { ContactFormType } from '../types';

import { ContactAddressFormSection } from '../components/ContactAddressFormSection';
import { ContactCommentsFormSection } from '../components/ContactCommentsFormSection';
import { ContactCustomAgreementFormSection } from '../components/ContactCustomAgreementFormSection';
import { ContactEmailsFormSection } from '../components/ContactEmailsFormSection';
import { formContainerStyle, formSectionsStyle } from '../components/contactFormSections.css';
import { ContactPersonalInfoFormSection } from '../components/ContactPersonalInfoFormSection';
import {
  OrganisationInfoAddress,
  OrganisationInfoCard,
  OrganisationInfoCreditRating,
  OrganisationInfoGeneral,
  OrganisationInfoPeppol,
} from '../components/OrganisationInfoCard';
import { mapContactFormDataToPayload, mapContactResponseToFormData } from '../utils';

type Props = {
  client: OrganisationClient;
};

export const CreateContactForm = ({ client }: Props) => {
  const mutation = useCreateContact(client.id);
  const cachedCreditsafeRecord = useCachedCreditsafeRecord(client);
  const creditsafeRecord = client.creditsafeRecord ?? cachedCreditsafeRecord;

  // When a contact is duplicated, pre-fill the form with the data in location state ({ duplicateContact: Contact; }).
  const { state } = useLocation();
  const duplicateContact = (state?.duplicateContact as Contact | undefined) ?? null;
  const duplicateContactFormValues = duplicateContact ? mapContactResponseToFormData(duplicateContact, client) : null;

  const form = useForm<ContactFormType>({
    defaultValues: {
      firstName: '',
      lastName: '',
      function: '',
      language: duplicateContactFormValues?.language ?? 'nl',
      phoneNumber: '',
      useOrganisationAddress: duplicateContactFormValues?.useOrganisationAddress ?? true,
      addressIndex: duplicateContactFormValues?.addressIndex ?? 0,
      address: duplicateContactFormValues?.address ?? {
        street: '',
        number: '',
        zipCode: '',
        city: '',
        countryCode: 'BE',
      },
      emails: duplicateContactFormValues?.emails ?? [
        {
          type: 'client',
          emailAddress: '',
          enableForQuotations: true,
          enableForInvoicesAndCreditNotes: true,
          enableForPaymentReminders: true,
        },
      ],
      customAgreement: false,
      comments: duplicateContactFormValues?.comments ?? '',
    },
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
  } = form;

  const navigate = useNavigate();
  const { t } = useTranslation(['clients', 'common']);

  const searchParams = useSearchParams()[0];
  const redirectPath = searchParams.get(SearchParamKeys.REDIRECT_PATH);

  const onSubmit = handleSubmit((data) => {
    if (mutation.isPending) return;

    mutation.mutate(mapContactFormDataToPayload(data, getValues('useOrganisationAddress'), client.addresses), {
      onSuccess: (contact) => {
        toast.success({ description: t('clients:alerts.successContactCreated') });
        navigate(redirectPath ?? r(routes.showClient, { clientId: client.id }), {
          state: {
            ...bypassBlocker,
            contactId: contact.id,
          },
        });
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  });

  const isBelgianOrganisation = client.addresses.find(({ type }) => type === 'headquarters')?.countryCode === 'BE';

  return (
    <>
      <Header.Root>
        <Header.Content backTo={r(routes.showClient, { clientId: client.id })} title={client.name} />

        <Header.Actions>
          <Header.Action icon="PersonAdd" loading={mutation.isPending} onClick={onSubmit}>
            {t('clients:createContact.submit')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Blocker isBlocked={isDirty || !!duplicateContact} message={t('clients:createContact.blocker')} />

      <Container className={formContainerStyle}>
        <OrganisationInfoCard>
          <OrganisationInfoGeneral
            companyName={client.name}
            otherNumber={client.otherNumber}
            vatInformationLastUpdatedAt={client.vatNumber !== null ? client.vatInformationLastUpdatedAt : null}
            vatNumber={client.vatNumber}
          />
          <OrganisationInfoAddress address={client.addresses.find((address) => address.type === 'headquarters') ?? client.addresses[0]} />
          {isBelgianOrganisation && <OrganisationInfoPeppol peppolId={client.peppolId} />}
          <OrganisationInfoCreditRating
            creditsafeRecord={creditsafeRecord}
            creditsafeStatus={creditsafeRecord !== null ? 'Success' : client.creditsafeStatus}
          />
        </OrganisationInfoCard>

        <Form autoComplete="off" onSubmit={onSubmit}>
          <div className={formSectionsStyle}>
            <ContactPersonalInfoFormSection form={form} />

            <Separator />

            <ContactAddressFormSection addresses={client.addresses} form={form} />

            <Separator />

            <ContactEmailsFormSection form={form} peppol={client.peppolId !== null} />

            <Separator />

            <ContactCustomAgreementFormSection form={form} />

            <Separator />

            <ContactCommentsFormSection form={form} />
          </div>
        </Form>
      </Container>
    </>
  );
};
